import { useEffect, useState } from 'react';
import { MinusIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useToast } from '@/components/Toast';
import { parseLocalDate, toYmd } from '@/lib/localDate';
import type { SignupSheet, AddSlotPayload } from '@flock-types/signupSheets.types';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_CHIPS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEK_PRESETS = ['4', '8', '12', '16'];
const MAX_BULK = 52; // backend cap

interface Props {
  sheet: SignupSheet;
  addSlot: (payload: AddSlotPayload) => Promise<void>;
  addSlotsBulk: (slots: AddSlotPayload[]) => Promise<void>;
  isMutating: boolean;
  onClose: () => void;
}

/** Add Date/Item/Option — single or bulk (port of the Expo add-slot modal). */
export default function AddSlotModal({ sheet, addSlot, addSlotsBulk, isMutating, onClose }: Props) {
  const { showToast } = useToast();
  const [mode, setMode] = useState<'single' | 'bulk'>('single');
  const thing =
    sheet.sheet_type === 'date' ? 'Date' : sheet.sheet_type === 'survey' ? 'Option' : 'Item';

  // Single mode
  const [label, setLabel] = useState('');
  const [date, setDate] = useState(''); // YYYY-MM-DD from <input type="date">
  const [showExtraField, setShowExtraField] = useState(false);
  const [capacity, setCapacity] = useState(1);

  // Bulk date mode
  const [bulkDay, setBulkDay] = useState(0); // 0=Sun … 6=Sat
  const [bulkWeeks, setBulkWeeks] = useState('12');
  const [bulkStartDate, setBulkStartDate] = useState(() => toYmd(new Date()));
  // Bulk item mode
  const [bulkItems, setBulkItems] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleAddSingle = async () => {
    const trimmedLabel = label.trim() || undefined;
    if (!date && !trimmedLabel) {
      showToast(
        sheet.sheet_type === 'date' ? 'Please select a date.' : 'Please enter a label.',
        'error'
      );
      return;
    }
    try {
      // date is already a plain YYYY-MM-DD string — pass through untouched
      await addSlot({ label: trimmedLabel, slotDate: date || undefined, capacity });
      onClose();
    } catch {
      showToast('Failed to add slot. Please try again.', 'error');
    }
  };

  const handleAddBulk = async () => {
    if (sheet.sheet_type === 'date') {
      const weeks = Math.min(parseInt(bulkWeeks, 10) || 12, MAX_BULK);
      // Local-date arithmetic only — first matching weekday ≥ start, then +7d
      const start = parseLocalDate(bulkStartDate);
      const dayDiff = (bulkDay - start.getDay() + 7) % 7;
      const firstDate = new Date(start);
      firstDate.setDate(firstDate.getDate() + dayDiff);

      const slots: AddSlotPayload[] = [];
      for (let i = 0; i < weeks; i++) {
        const slotDate = new Date(firstDate);
        slotDate.setDate(slotDate.getDate() + i * 7);
        slots.push({ slotDate: toYmd(slotDate), capacity });
      }
      try {
        await addSlotsBulk(slots);
        onClose();
      } catch {
        showToast('Failed to add dates. Please try again.', 'error');
      }
    } else {
      const items = bulkItems
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
      if (items.length === 0) {
        showToast('Please enter at least one item.', 'error');
        return;
      }
      if (items.length > MAX_BULK) {
        showToast(`You can add up to ${MAX_BULK} items at once.`, 'error');
        return;
      }
      try {
        await addSlotsBulk(items.map((l) => ({ label: l, capacity })));
        onClose();
      } catch {
        showToast('Failed to add items. Please try again.', 'error');
      }
    }
  };

  const inputClass =
    'w-full rounded-lg border border-flock-gray-200 bg-flock-navy-50 px-3 py-2 text-sm outline-none focus:border-flock-navy-400';

  const bulkItemCount = bulkItems.split('\n').filter((s) => s.trim()).length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold text-flock-navy-900">
            {mode === 'single' ? `Add ${thing}` : `Add Multiple ${thing}s`}
          </h2>
          <button onClick={onClose} className="p-1 text-flock-navy-400 hover:text-flock-navy-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Mode toggle */}
        <div className="mb-4 flex overflow-hidden rounded-lg border border-flock-gray-200">
          {(['single', 'bulk'] as const).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={clsx(
                'flex-1 py-2 text-sm font-medium transition',
                mode === m
                  ? 'bg-flock-navy-600 text-white'
                  : 'bg-flock-gray-100 text-flock-navy-600 hover:bg-flock-gray-200'
              )}
            >
              {m === 'single' ? 'Single' : `Multiple ${thing}s`}
            </button>
          ))}
        </div>

        {mode === 'single' ? (
          <div className="space-y-3">
            {sheet.sheet_type === 'date' ? (
              <>
                <div>
                  <label className="mb-1 block text-xs font-medium text-flock-navy-600">Date</label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className={inputClass}
                  />
                </div>
                {!showExtraField ? (
                  <button
                    onClick={() => setShowExtraField(true)}
                    className="text-sm text-flock-navy-400 hover:text-flock-navy-600"
                  >
                    + Add description
                  </button>
                ) : (
                  <input
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    maxLength={255}
                    placeholder="Description (optional)"
                    className={inputClass}
                  />
                )}
              </>
            ) : (
              <>
                <div>
                  <label className="mb-1 block text-xs font-medium text-flock-navy-600">
                    {thing}
                  </label>
                  <input
                    autoFocus
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    maxLength={255}
                    placeholder="e.g., Paper plates (x50)"
                    className={inputClass}
                  />
                </div>
                {!showExtraField ? (
                  <button
                    onClick={() => setShowExtraField(true)}
                    className="text-sm text-flock-navy-400 hover:text-flock-navy-600"
                  >
                    + Add date
                  </button>
                ) : (
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className={inputClass}
                  />
                )}
              </>
            )}
          </div>
        ) : sheet.sheet_type === 'date' ? (
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-flock-navy-600">Every</label>
              <div className="flex flex-wrap gap-2">
                {DAY_CHIPS.map((day, i) => (
                  <button
                    key={day}
                    onClick={() => setBulkDay(i)}
                    className={clsx(
                      'rounded-lg px-4 py-2 text-sm font-medium transition',
                      bulkDay === i
                        ? 'bg-flock-navy-600 text-white'
                        : 'bg-flock-gray-200 text-flock-navy-600 hover:bg-flock-gray-300'
                    )}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-flock-navy-600">
                Starting from
              </label>
              <input
                type="date"
                value={bulkStartDate}
                onChange={(e) => setBulkStartDate(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-flock-navy-600">
                For how many weeks?
              </label>
              <div className="flex gap-2">
                {WEEK_PRESETS.map((w) => (
                  <button
                    key={w}
                    onClick={() => setBulkWeeks(w)}
                    className={clsx(
                      'rounded-lg px-4 py-2 text-sm font-medium transition',
                      bulkWeeks === w
                        ? 'bg-flock-navy-600 text-white'
                        : 'bg-flock-gray-200 text-flock-navy-600 hover:bg-flock-gray-300'
                    )}
                  >
                    {w}
                  </button>
                ))}
                <input
                  type="number"
                  min={1}
                  max={MAX_BULK}
                  value={bulkWeeks}
                  onChange={(e) => setBulkWeeks(e.target.value)}
                  className="w-20 rounded-lg border border-flock-gray-200 bg-flock-navy-50 px-3 py-2 text-sm outline-none focus:border-flock-navy-400"
                />
              </div>
              <p className="mt-1 text-xs text-flock-navy-400">
                Will add {Math.min(parseInt(bulkWeeks, 10) || 12, MAX_BULK)} {DAY_NAMES[bulkDay]}s
              </p>
            </div>
          </div>
        ) : (
          <div>
            <label className="mb-1 block text-xs font-medium text-flock-navy-600">
              One {thing.toLowerCase()} per line
            </label>
            <textarea
              autoFocus
              value={bulkItems}
              onChange={(e) => setBulkItems(e.target.value)}
              rows={6}
              placeholder={'Paper plates\nNapkins\nCups\nJuice boxes'}
              className={inputClass}
            />
            {bulkItemCount > 0 && (
              <p className="mt-1 text-xs text-flock-navy-400">
                {bulkItemCount} {thing.toLowerCase()}
                {bulkItemCount !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        )}

        {/* Capacity (hidden for surveys — unlimited votes per option) */}
        {sheet.sheet_type !== 'survey' && (
          <div className="mt-4 flex items-center gap-3">
            <span className="text-sm font-medium text-flock-navy-700">
              Spots {mode === 'bulk' ? 'per slot' : 'available'}
            </span>
            <div className="flex items-center rounded-lg border border-flock-gray-300 bg-flock-gray-100">
              <button
                onClick={() => setCapacity((c) => Math.max(1, c - 1))}
                className="px-3 py-2 text-flock-navy-600 hover:text-flock-navy-800"
                title="Fewer spots"
              >
                <MinusIcon className="h-4 w-4" />
              </button>
              <span className="min-w-[2rem] text-center text-sm font-medium text-flock-navy-800">
                {capacity}
              </span>
              <button
                onClick={() => setCapacity((c) => Math.min(100, c + 1))}
                className="px-3 py-2 text-flock-navy-600 hover:text-flock-navy-800"
                title="More spots"
              >
                <PlusIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        <div className="mt-5 flex gap-3">
          <button
            onClick={onClose}
            className="min-h-touch flex-1 rounded-lg bg-flock-gray-200 px-4 py-2.5 text-sm font-medium text-flock-navy-700 transition hover:bg-flock-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={mode === 'single' ? handleAddSingle : handleAddBulk}
            disabled={isMutating}
            className="min-h-touch flex-1 rounded-lg bg-flock-navy-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-flock-navy-700 disabled:opacity-50"
          >
            {isMutating ? 'Adding…' : mode === 'bulk' ? 'Add All' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
}
